/* eslint-disable custom/no-hardcoded-colors */
import { Icon, styled, theme } from 'design-system';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { Journal } from '../../../resources/journal';

const DropdownContainer = styled('div', {
  paddingLeft: 0,
  paddingBottom: 0,
  borderRadius: '10px 10px 0 0',
  position: 'relative',
});

const DropdownTrigger = styled('div', {
  border: '1px solid #C4D1F0',
  borderRadius: '10px',
  padding: '14px',
  fontSize: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  width: '360px',
  backgroundColor: '#FFF',
  transition: 'border-color 0.2s ease',
  position: 'relative',

  variants: {
    isOpen: {
      true: {
        borderColor: theme.colors.blue9.value,
        borderBottomLeftRadius: '0px',
        borderBottomRightRadius: '0px',
        overflow: 'visible',
      },
      false: {
        borderColor: '#C4D1F0',
      },
    },
  },

  '&:hover': {
    borderColor: theme.colors.blue9.value,
  },

  '&:hover:not([data-is-open="true"])': {
    borderColor: theme.colors.blue9.value,
  },
});

const ChevronIcon = styled('span', {
  width: '16px',
  height: '9px',
  color: 'rgba(93, 106, 129, 1)',
  transition: 'transform 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    isOpen: {
      true: {
        transform: 'rotate(180deg)',
      },
      false: {
        transform: 'rotate(0deg)',
      },
    },
  },
});

const Search = styled('input', {
  width: '100%',
  color: theme.colors.gray12,
  gap: '0px',
  outline: 'none',
  border: '2px solid #e6e6e6',
  backgroundColor: theme.colors.white,
  fontSize: 14,
  background: 'none',
  fontWeight: '400',
  justifyContent: 'flex-start',
  height: 40,
  fontFamily: 'IBM Plex Sans',
  borderRadius: '6px',
  padding: '0.5rem 0.75rem',
});

const JournalList = styled('div', {
  maxHeight: '217px',
  overflowY: 'auto',
  gap: '6px',
  paddingTop: 10,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '6px',

  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.colors.gray1,
  },
  '&::-webkit-scrollbar-thumb': {
    background: `${theme.colors.gray6}`,
    height: '40px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.colors.gray10,
  },
});

const JournalListItem = styled('div', {
  width: '99%',
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '10px',
  padding: '12px 10px',
  cursor: 'pointer',
  color: 'rgba(93, 106, 129, 1)',
  '&:hover': {
    backgroundColor: 'rgba(217, 229, 253, 1)',
    color: `${theme.colors.blue11.value}`,
  },
});

const InputBoxLabel = styled('div', {
  fontWeight: 600,
  color: 'rgba(5, 30, 87, 1)',
  lineHeight: '24px',
  fontSize: '16px',
  marginBottom: '8px',
  display: 'block',
  marginTop: '16px',
  width: '360px',
});

const RequiredStyling = styled('span', {
  fontWeight: 400,
  color: 'rgba(5, 30, 87, 1)',
  lineHeight: '24px',
  fontSize: '14px',
});

export const ChangeArticleConfig = ({
  journal,
  onOptionSelect,
}: {
  journal: Journal;
  onOptionSelect: (selectedId: string) => void;
}) => {
  const t = useTranslation('homepage').t;
  const articleTypes = (journal.articleTypes ?? []).map((type) => ({
    master: type.master ?? false,
    id: type.id?.toString() ?? '',
    name: type.name ?? '',
    isSelected: type.master || false,
  }));

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState(
    articleTypes.find((type) => type.master) || articleTypes[0] || null,
  );

  const filteredOptions = articleTypes.filter((type) =>
    type.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelectOption = (selectedType: {
    master: boolean;
    id: string;
    name: string;
    isSelected: boolean;
  }) => {
    setSelectedOption(selectedType);
    onOptionSelect(selectedType.id);
    setIsOpen(false);
  };

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length > maxLength) {
      return `${text.substring(0, maxLength)}...`;
    }
    return text;
  };

  return (
    <div style={{ marginTop: '26px' }}>
      <InputBoxLabel>
        {t('section1_v2.select_article_type', 'Select Article type')}{' '}
        <RequiredStyling>*</RequiredStyling>
      </InputBoxLabel>

      <DropdownContainer
        ref={dropdownRef}
        style={{
          position: 'relative',
          zIndex: isOpen ? 999 : 'auto',
        }}
      >
        <DropdownTrigger
          data-test-id="select-article-type-dropdown-trigger"
          onClick={handleToggleDropdown}
          isOpen={isOpen}
          data-is-open={isOpen.toString()}
        >
          {!isOpen ? (
            <>
              {selectedOption ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    justifyItems: 'center',
                    flexDirection: 'row',
                    width: '100%',
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center' }}
                    title={selectedOption.name}
                  >
                    {truncateText(selectedOption.name, 30)}
                  </div>
                  <ChevronIcon isOpen={isOpen}>
                    <Icon variant={'regular'} name="chevronDown" />
                  </ChevronIcon>
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    justifyItems: 'center',
                    flexDirection: 'row',
                    width: '100%',
                  }}
                >
                  <span>Select Article Type</span>
                  <ChevronIcon isOpen={isOpen}>
                    <Icon variant={'regular'} name="chevronDown" />
                  </ChevronIcon>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Closed state display */}
              {selectedOption ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    justifyItems: 'center',
                    flexDirection: 'row',
                    width: '100%',
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center' }}
                    title={selectedOption.name}
                  >
                    {truncateText(selectedOption.name, 30)}
                  </div>
                  <ChevronIcon isOpen={isOpen}>
                    <Icon variant={'regular'} name="chevronDown" />
                  </ChevronIcon>
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    justifyItems: 'center',
                    flexDirection: 'row',
                    width: '100%',
                  }}
                >
                  <span>Select Article Type</span>
                  <ChevronIcon isOpen={isOpen}>
                    <Icon variant={'regular'} name="chevronDown" />
                  </ChevronIcon>
                </div>
              )}
            </>
          )}
        </DropdownTrigger>

        {/* Dropdown content as overlay */}
        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              width: '360px',
              backgroundColor: '#FFF',
              border: '1px solid #C4D1F0',
              borderTop: 'none',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              zIndex: 1000,
              maxHeight: '400px',
              overflowY: 'auto',
              boxShadow: '0px 4px 24px -8px rgba(67, 84, 117, 0.35)',
            }}
          >
            <div style={{ padding: '10px' }}>
              <div
                style={{
                  position: 'relative',
                  backgroundColor: 'rgba(244, 244, 244, 1)',
                  borderRadius: '10px',
                }}
              >
                <Search
                  placeholder={t('common.type_to_search', 'Type to search...')}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onFocus={(e) => {
                    e.stopPropagation();
                  }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    backgroundColor: '#FFF',
                    color: 'black',
                    border: '1px solid #C4D1F0',
                    width: '100%',
                  }}
                  id="search_for_article_type"
                  data-test-id="search-input"
                  type="text"
                  aria-label="Search for article type"
                />
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    position: 'absolute',
                    right: '0px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor:
                      searchTerm.length > 0 ? '#0054F1' : '#0062FF1A',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    borderTopRightRadius: '5px',
                    borderBottomRightRadius: '5px',
                  }}
                >
                  <div
                    style={{
                      color:
                        searchTerm.length > 0
                          ? '#FFF'
                          : 'rgba(93, 106, 129, 1)',
                    }}
                  >
                    <Icon name="search" size="sm" variant={'regular'} />
                  </div>
                </div>
              </div>

              {filteredOptions.length > 0 && (
                <JournalList>
                  {filteredOptions.map((type) => (
                    <JournalListItem
                      key={type.id}
                      data-test-id={`article-type-item-${type.id}`}
                      title={type.name}
                      onClick={(
                        e: React.MouseEvent<HTMLDivElement, MouseEvent>,
                      ) => {
                        e.stopPropagation();
                        handleSelectOption(type);
                      }}
                      style={
                        selectedOption?.id === type.id
                          ? {
                              backgroundColor: 'rgba(217, 229, 253, 1)',
                              color: 'rgba(0, 84, 241, 1)',
                            }
                          : {}
                      }
                      aria-label={`Article Type ${type.name}`}
                    >
                      <span
                        style={{
                          width: '94%',
                          fontSize: '14px',
                          lineHeight: '24px',
                          fontWeight: 500,
                        }}
                      >
                        {truncateText(type.name, 35)}
                      </span>
                      {selectedOption?.id === type.id && (
                        <span>
                          <Icon name={'check'} variant={'solid'} />
                        </span>
                      )}
                    </JournalListItem>
                  ))}
                </JournalList>
              )}

              {filteredOptions.length === 0 && searchTerm.length >= 3 && (
                <div
                  style={{
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      textAlign: 'center',
                      color: 'rgba(5, 30, 87, 1)',
                    }}
                  >
                    Sorry, no results were found
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 400,
                      textAlign: 'center',
                      color: 'rgba(5, 30, 87, 1)',
                    }}
                  >
                    You can search for another article type
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DropdownContainer>
    </div>
  );
};

// eslint-disable-next-line custom/no-hardcoded-colors
